import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import { useIAmShopAssignments } from 'hooks'
import { Icon } from 'packages/eid-icons'
import DeleteAssignment from './DeleteAssignment'
import { PageContextProvider } from 'pageContext'
import useSubcomponents from 'useSubcomponents'
import EidTableGrid from 'components/EidTableGrid/EidTableGrid'
import { Filters } from 'components'
import AddNewAssignment from 'components/Popups/AddNewAssignment/AddNewAssignment'
import { RESOURCE_TYPE_NAMES } from 'utils'

const canAddIAMShopAssignmentName =
    'ResourceAdmin-SharedFolderDetails-IAMShopAssignments-AddAssignment-Control'
const canDeleteIAMShopAssignmentName =
    'ResourceAdmin-SharedFolderDetails-IAMShopAssignments-DeleteAssignment-Control'

export const IAmShopAssignments = ({ resource }) => {
    const { t } = useTranslation()
    const {
        list,
        loading: isLoading,
        pagination,
    } = useIAmShopAssignments(resource.id)

    const { hasAccessToControl, getAccessBasedAttributes } = useSubcomponents()

    const attributes = [
        {
            label: t('IAmShopPermissionLevel'),
            name: 'localizedAccessLevelFriendlyName',
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-SharedFolderDetails-IAMShopAssignments-PermissionLevel',
            },
        },
        {
            label: t('Assignee'),
            name: 'assigneePreviewName',
            sortable: true,
            //NOTE : Keeping the commented out code for subcomponent traceability
            // requireAccess: {
            //     control:
            //         'ResourceAdmin-SharedFolderDetails-IAMShopAssignments-Assignee',
            // },
        },
        {
            label: t('AssigneeTechnicalName'),
            name: 'assigneeTechnicalPreviewName',
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-SharedFolderDetails-IAMShopAssignments-AssigneeTechnicalName',
            },
        },
        {
            label: t('AccessFulfilledByAssignee'),
            name: 'parentAssigneePreviewName',
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-SharedFolderDetails-IAMShopAssignments-AccessFulfilledByAssignee',
            },
        },
        {
            name: 'resourceSystemFriendlyName',
            label: t('ResourceDisplayName'),
            sortable: true,
            requireAccess: {
                control:
                    'ResourceAdmin-SharedFolderDetails-IAMShopAssignments-Resource',
            },
        },
        {
            hideValue: true,
            requireAccess: {
                control:
                    'ResourceAdmin-SharedFolderDetails-IAMShopAssignments-DeleteAssignment-Control',
            },
            component: {
                componentName: 'renderer',
                render: (item) => {
                    return (
                        <Box display={'flex'}>
                            {hasAccessToControl(
                                canDeleteIAMShopAssignmentName,
                            ) &&
                                item.assigneeType === 'Person' && (
                                    <DeleteAssignment
                                        resource={resource}
                                        item={item}
                                        drawerView={false}
                                    />
                                )}
                        </Box>
                    )
                },
            },
        },
    ]

    return (
        <>
            <Box
                width="100%"
                display="flex"
                alignItems="space-between"
                justifyContent="space-between"
                paddingBottom="16px"
            >
                {hasAccessToControl(canAddIAMShopAssignmentName) && (
                    <PageContextProvider key="AddNewIAmShopAssignments">
                        <AddNewAssignment
                            resourceType={RESOURCE_TYPE_NAMES.SHARED_FOLDER}
                            labelKey={'AddNewAccessAssignment'}
                            searchUrl={`api/people/allUnassignedPeopleByResourceAccessRequestAssignee`}
                            targetAssigneeId={resource.id}
                            showPermissionLevel={true}
                            permissionLevelProps={{
                                permissionLabelProp:
                                    'localizedAccessLevelFriendlyName',
                            }}
                            filterWithAccessLevel={true}
                            startIcon={
                                <Icon
                                    name={'Plus'}
                                    fill="white"
                                    style={{
                                        width: '1.6rem',
                                        height: '1.6rem',
                                        marginRight: '0.5rem',
                                    }}
                                />
                            }
                        />
                    </PageContextProvider>
                )}

                <Box width="308px" display="flex" alignItems="center">
                    <Filters.TextSearch outlined />
                </Box>
            </Box>

            <EidTableGrid
                pagination={pagination}
                isLoading={isLoading}
                list={list}
                attributes={getAccessBasedAttributes(attributes)}
                headings={getAccessBasedAttributes(attributes).map(
                    (a) => a.label,
                )}
            />
        </>
    )
}
